import React from 'react';
import styles from './CoverageHeatmapLegendPanel.module.scss';

export type HeatmapMetricMode = 'max' | 'avg' | 'min' | 'count' | 'duration' | 'std';

export interface LegendBin {
  color: string;
  label: string;
}

interface Props {
  metricMode: HeatmapMetricMode;
  onMetricModeChange: (mode: HeatmapMetricMode) => void;
  visible: boolean;
  sectionTab: 'simple' | 'gap' | 'revisit';
  dynamicBins?: LegendBin[];
}

export const CoverageHeatmapLegendPanel: React.FC<Props> = ({
  metricMode,
  onMetricModeChange,
  visible,
  sectionTab,
  dynamicBins,
}) => {
  if (!visible) return null;

  const isGap = sectionTab === 'gap';
  const isRevisit = sectionTab === 'revisit';

  const title = isGap ? 'Gap' : isRevisit ? 'Revisit' : 'Coverage';

  const gapOptions = [
    { value: 'max', label: 'Maximum' },
    { value: 'avg', label: 'Average' },
    { value: 'min', label: 'Minimum' },
  ];

  const revisitTypeOptions = [
    { value: 'duration', label: 'Duration' },
    { value: 'count', label: 'Count' },
  ];

  const revisitType = metricMode === 'count' ? 'count' : 'duration';

  const fallbackBins = [
    { color: '#991b1b', label: '< 40s' },
    { color: '#d97706', label: '40 - 60s' },
    { color: '#eab308', label: '60 - 80s' },
    { color: '#84cc16', label: '80 - 100s' },
    { color: '#22c55e', label: '> 100s' },
  ];

  const bins = dynamicBins && dynamicBins.length > 0 ? dynamicBins : fallbackBins;

  return (
    <div className={styles.legendCard}>
      <div className={styles.legendHeader}>
        <span className={styles.legendTitle}>{title}</span>
      </div>

      {isGap && (
        <div style={{ marginBottom: 12 }}>
          <select
            className={styles.dropdownSelect}
            value={metricMode}
            onChange={(e) => onMetricModeChange(e.target.value as HeatmapMetricMode)}
          >
            {gapOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {isRevisit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: 12 }}>
          <select
            className={styles.dropdownSelect}
            value={revisitType}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'count') {
                onMetricModeChange('count');
              } else {
                onMetricModeChange('max');
              }
            }}
          >
            {revisitTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {revisitType === 'duration' && (
            <select
              className={styles.dropdownSelect}
              value={metricMode}
              onChange={(e) => onMetricModeChange(e.target.value as HeatmapMetricMode)}
            >
              {gapOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className={styles.legendList}>
        {bins.map((bin, i) => (
          <div key={i} className={styles.legendItem}>
            <div className={styles.colorSwatch} style={{ backgroundColor: bin.color }} />
            <span className={styles.rangeLabel}>{bin.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
